'use client';

import React, { useMemo } from 'react';
import { GmailAccount } from '@/contexts/gmail-account-context';
import { Mail } from 'lucide-react';
import { cn } from '@/lib/utils';
import { SELECT_BASE_STYLES } from '@/lib/styles/select-styles';

/**
 * Props for the GmailAccountSelector component.
 */
interface GmailAccountSelectorProps {
  /** Array of Gmail accounts to display in the selector */
  accounts: GmailAccount[];
  /** Currently selected account ID */
  selectedId: string | null;
  /** Callback function called when an account is selected */
  onSelect: (accountId: string | null) => void;
  /** Whether the selector is in a loading state */
  loading?: boolean;
  /** Additional CSS classes to apply to the container */
  className?: string;
  /** Size variant of the selector */
  size?: 'sm' | 'md';
  /** Whether to show the label above the selector */
  showLabel?: boolean;
}

/**
 * Gmail account selector dropdown component.
 *
 * Displays a dropdown to select between multiple Gmail accounts.
 * Automatically hides when there's only one account or when loading.
 *
 * @example
 * ```tsx
 * <GmailAccountSelector
 *   accounts={gmailAccounts}
 *   selectedId={selectedId}
 *   onSelect={setSelectedId}
 *   size="sm"
 * />
 * ```
 *
 * @see {@link GmailAccountContext} for account management
 */
export const GmailAccountSelector = React.memo(function GmailAccountSelector({
  accounts,
  selectedId,
  onSelect,
  loading = false,
  className = '',
  size = 'md',
  showLabel = false,
}: GmailAccountSelectorProps) {
  const sizeStyles = useMemo(
    () => (size === 'sm' ? 'h-8 px-2 py-1 text-xs max-w-[180px]' : 'h-10 w-full px-3 py-2 text-sm'),
    [size]
  );

  if (loading || accounts.length <= 1) {
    return null;
  }

  return (
    <div className={className}>
      {showLabel && (
        <label
          id="gmail-account-label"
          className="text-xs text-muted-foreground flex items-center gap-2 mb-2"
        >
          <Mail className="h-3 w-3" aria-hidden="true" />
          Gmail Account
        </label>
      )}
      <select
        value={selectedId || ''}
        onChange={(e) => onSelect(e.target.value || null)}
        className={cn(SELECT_BASE_STYLES, sizeStyles)}
        disabled={loading}
        aria-label={showLabel ? undefined : 'Select Gmail account'}
        aria-labelledby={showLabel ? 'gmail-account-label' : undefined}
      >
        {accounts.map((account) => (
          <option key={account.id} value={account.id}>
            {account.email} {account.is_primary ? '(Primary)' : ''}
          </option>
        ))}
      </select>
    </div>
  );
});
